import { t } from "@lingui/macro"
import { Box, Typography, styled, Button } from "@mui/material"
import { observer } from "mobx-react"
import { useCallback, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"

import { PropertyDetailStore } from "./store"
import { IKnowledgeItem } from "./types"

import { Table } from "src/components/Table"
import { NoWrap } from "src/components/NoWrap"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { GlobalStore } from "src/store"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { useInitializer } from "src/lib/initializer"
import { KnowledgeItemModal } from "src/modals/knowledge-items"
import { ConfirmModal } from "src/modals/confirm"
import { useSegmentTypeTranslations } from "src/components/SegmentPicker/hooks/useSegmentTypeTranslations"

const HeaderWrapper = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}))

const BreadcrumbContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(1),
}))

const BackCursor = styled(Box)({
    cursor: "pointer",
    "&:hover": {
        textDecoration: "underline",
    },
})

const View = observer(() => {
    const { id } = useParams<{ id: string }>()
    const store = useStore(PropertyDetailStore)
    const globalStore = useStore(GlobalStore)
    const translations = useSegmentTypeTranslations()
    const navigate = useNavigate()

    const segmentId = parseInt(id ?? "0", 10)

    const handleBackToProperties = useCallback(() => {
        navigate("/chatbot/properties")
    }, [navigate])

    const initialized = useInitializer(async () => {
        if (segmentId > 0) {
            await store.init(
                globalStore.session.accessGroup.id ?? DEFAULT_ACCESS_GROUP.id,
                segmentId,
            )
        }
    }, [globalStore.session.accessGroup, segmentId])

    const handleNewKnowledgeItemClick = useCallback(() => {
        globalStore.modals.open(
            () => (
                <KnowledgeItemModal
                    preselectedValues={{
                        agentAccess: "qa_agent",
                        knowledgeType: "page",
                        segments: segmentId > 0 ? [segmentId] : [],
                    }}
                />
            ),
            {
                onClose: async () => {
                    await store.knowledgeItems.reload()
                },
            },
        )
    }, [globalStore.modals, segmentId, store])

    const handleEditKnowledgeItem = useCallback(
        (item: IKnowledgeItem) => {
            globalStore.modals.open(() => <KnowledgeItemModal id={item.id} />, {
                onClose: async () => {
                    await store.knowledgeItems.reload()
                },
            })
        },
        [globalStore.modals, store],
    )

    const handleDeleteKnowledgeItem = useCallback(
        (item: IKnowledgeItem) => {
            globalStore.modals.open(
                () => (
                    <ConfirmModal
                        onConfirm={async (confirmed) => {
                            if (confirmed) {
                                await store.deleteKnowledgeItem(item.id)
                            }
                        }}
                        title={t`property-detail.delete.are-you-sure`}
                        content={`${t`property-detail.delete.confirm-delete-item`} ${
                            item.title
                        }`}
                    />
                ),
                {
                    variant: "slide-up-w600",
                },
            )
        },
        [globalStore.modals, store],
    )

    const rowActionsRenderer = useCallback(
        (item: IKnowledgeItem) => [
            {
                text: t`property-detail.actions.edit`,
                onClick: () => handleEditKnowledgeItem(item),
            },
            {
                text: t`property-detail.actions.delete`,
                onClick: () => handleDeleteKnowledgeItem(item),
            },
        ],
        [handleEditKnowledgeItem, handleDeleteKnowledgeItem],
    )

    const renderCustomHeader = useMemo(() => {
        const segmentTypeText =
            store.segmentType !== null
                ? translations[store.segmentType as keyof typeof translations]
                : t`property-detail.header`

        const segmentName = store.segmentName ?? `${segmentTypeText} Card`

        return (
            <HeaderWrapper>
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{ fontWeight: 500, color: "grey.900" }}
                >
                    {`${segmentTypeText} Card`}
                </Typography>
                <BreadcrumbContainer>
                    <Typography variant="body1" color="grey.700">
                        {t`navigation.chatbot`} /
                    </Typography>
                    <BackCursor onClick={handleBackToProperties}>
                        <Typography
                            variant="body1"
                            color="grey.700"
                            sx={{ ml: 1 }}
                        >
                            {t`navigation.properties`} /
                        </Typography>
                    </BackCursor>
                    <Typography variant="body1" color="grey.900" sx={{ ml: 1 }}>
                        {segmentName}
                    </Typography>
                </BreadcrumbContainer>
            </HeaderWrapper>
        )
    }, [
        store.segmentType,
        store.segmentName,
        translations,
        handleBackToProperties,
    ])

    if (segmentId <= 0) {
        return (
            <div>
                {renderCustomHeader}
                <Box sx={{ p: 3 }}>
                    <Typography color="error">
                        {t`property-detail.invalid-id`}
                    </Typography>
                </Box>
            </div>
        )
    }

    return (
        <div>
            {renderCustomHeader}
            <Box
                sx={{
                    p: 3,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                }}
            >
                <Typography variant="h6">
                    {t`property-detail.knowledge-items.title`}
                </Typography>
                <Button
                    variant="contained"
                    onClick={handleNewKnowledgeItemClick}
                >
                    + {t`property-detail.create-knowledge-item`}
                </Button>
            </Box>
            <Box sx={{ p: 3, pt: 0 }}>
                {!initialized ? (
                    <Typography>{t`property-detail.loading`}</Typography>
                ) : (
                    <Table
                        paginator={store.knowledgeItems}
                        onRowClick={handleEditKnowledgeItem}
                        rowActionsRenderer={rowActionsRenderer}
                        headers={[
                            {
                                key: "id",
                                name: t`property-detail.knowledge-items.column.id`,
                            },
                            {
                                key: "title",
                                name: t`property-detail.knowledge-items.column.title`,
                            },
                            {
                                key: "type",
                                name: t`property-detail.knowledge-items.column.type`,
                            },
                        ]}
                        rowRenderer={(item) => {
                            return {
                                id: <NoWrap>{item.id}</NoWrap>,
                                title: (
                                    <NoWrap title={item.title}>
                                        {item.title}
                                    </NoWrap>
                                ),
                                type: (
                                    <NoWrap>
                                        {item.knowledge_item_type ?? "Unknown"}
                                    </NoWrap>
                                ),
                            }
                        }}
                        emptyStateText={t`property-detail.knowledge-items.empty`}
                    />
                )}
            </Box>
        </div>
    )
})

export const PropertyDetailView = () => (
    <StoreProvider Store={PropertyDetailStore}>
        <View />
    </StoreProvider>
)
